import { useEffect, useMemo } from "react";

import {
    BsMetaUtil,
    CollateralWithTerms,
    CreateLoanTxnParams,
    MarketGuideMode,
    OnboardingStep,
    OrderbookQuote,
    RoleView,
    ParsedMarketInformationWithLtvInfo,
    deserializeStrategyDurationOrUndefined,
    getAssetData,
    getZcMarketInformation,
    getBorrowCapDetails,
    getCollateralIdentifier,
    getCollateralMint,
    getClmmPositionType,
    useLoopscaleWalletContext,
    useTokenListContext,
    useCreateStrategyWithCollateralTransaction,
    useFillStrategyOfferTransaction,
    useMarketOracleInfos,
    useStrategyDurations,
    useSupportedCollateral,
    useUserOnboardingStep,
    getClmmPositionMint,
    isClmmCollateral,
    getVaultLpStakeUnstakeIxParams,
    isVaultLpCollateral
} from "@bridgesplit/abf-react";
import {
    JLP_MINT,
    LOADING_ERROR,
    Result,
    USDC_MINT,
    bpsToUiDecimals,
    decimalsToBps,
    filterNullableRecord,
    uiAmountToLamports,
    uiPercentToBps
} from "@bridgesplit/utils";
import { TokenListMetadata, CreateCustomStrategyTxnParams, TokenListTag, getDurationIndex } from "@bridgesplit/abf-sdk";
import { MEDIA } from "@bridgesplit/ui";
import { createSelector } from "@reduxjs/toolkit";
import { QueryStatus } from "@reduxjs/toolkit/dist/query";
import { useSelector } from "react-redux";
import { AppDialog, AppDialogData, useAppDialog } from "app/utils";
import { COPY } from "app/constants";
import { TrackEventWithProps, TrackTransactionEvent } from "app/types";
import {
    presetFormToMixpanelFormat,
    trackSubmitMarketBorrowOrder,
    trackSubmitMarketLend,
    usePermissionedLoanCheck,
    useTrack
} from "app/hooks";

import { useTransactionSender } from "../transactions";
import { MarketProps, RateTick } from "./types";
import { useMarketContext, useMarketContextOptional } from "./common";
import { MarketLendForm } from "./lend";
import { getTokenTagMetadata } from "../common";
import { BorrowMode, useMarketBorrowContext } from "./borrow";
import { RootState } from "../../reducers";

export const MARKET_DETAIL_BREAKPOINT = MEDIA.XL;

export function useMarketLend() {
    const createStrategy = useCreateStrategyWithCollateralTransaction();

    const { token } = useMarketContext();
    const {
        data: { userWalletAddress: user }
    } = useLoopscaleWalletContext();
    const { strategyDurations } = useStrategyDurations();

    const { getMarketInformationsByCollateralMint } = useMarketOracleInfos({
        filter: {
            principalMints: token ? [token.mint] : [],
            addresses: [],
            verified: true
        }
    });

    // const trackMixpanelEvent = useTrackFromTransactions();

    const send = useTransactionSender();

    /**
     * 1. generate strat create
     * 3. generate strat modify
     * 4. sign all txns in parrell
     * 5. send txn (only strat escrow create not sign in)
     */
    return async function lend({ form }: { form: MarketLendForm }) {
        if (!token || !user || !strategyDurations || !form.strategyDurationToApy)
            return Result.errFromMessage(LOADING_ERROR);
        const description = `Creating your ${COPY.STRATEGY_TERM.toLowerCase()}`;

        const offerTermsMixpanel = presetFormToMixpanelFormat(form.collateral, form.strategyDurationToApy);

        const depositAmountLamports = uiAmountToLamports(form.amount, token.decimals);

        const liquidityBufferLamports =
            form.liquidityBuffer && token.decimals
                ? uiAmountToLamports(form.liquidityBuffer, token.decimals)
                : undefined;

        const maxOriginationSizeLamports =
            form.originationCap && token.decimals ? uiAmountToLamports(form.originationCap, token.decimals) : undefined;

        const interestFee = form.interestFee ? decimalsToBps(form.interestFee, "cbps") : undefined;
        const originationFee = form.originationFee ? decimalsToBps(form.originationFee, "cbps") : undefined;

        const collateralTerms = getCollateralTerms(form, getMarketInformationsByCollateralMint);

        const params: CreateCustomStrategyTxnParams = {
            principalMint: token.mint,
            amount: depositAmountLamports,
            lender: user,
            liquidityBuffer: liquidityBufferLamports,
            interestFee: interestFee,
            originationFee: originationFee,
            originationCap: maxOriginationSizeLamports,
            externalYieldSourceArgs: {
                newExternalYieldSource: form.yieldSource,
                createExternalYieldAccount: true
            },
            collateralTerms: Array.from(collateralTerms.entries()),
            marketInformation: form.selectedMarketInfoAddress
        };

        const transactionResults = await send(createStrategy, params, {
            description,
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitMarketLend,
                params: trackSubmitMarketLend(params, offerTermsMixpanel)
            },
            sendOptions: {
                allowBatchFailure: false
            }
        });

        if (!transactionResults.isOk())
            return Result.errFromMessage(`Unable to create ${COPY.STRATEGY_TERM.toLowerCase()}`);

        return transactionResults;
    };
}

const BORROW_ERRORS = {
    "Rate limit": "You're starting loans too quickly - please wait a few seconds and try again",
    "Offer is no longer available": "Quote is no longer available",
    "Borrow cap": "Your request exceeds the available borrow capacity for this market",
    "You can only borrow": "Your quote exceeds the global borrow limit per loan",
    "You have insufficient collateral": "Your quote exceeds your borrow limit"
};

export function useMarketBorrow() {
    const send = useTransactionSender();
    const fillOrder = useFillStrategyOfferTransaction();
    const { token, isDialog } = useMarketContext();
    const { form, activeUserCollateral, bestQuote: quote, setForm } = useMarketBorrowContext();
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const { loanNonce } = usePermissionedLoanCheck(token, activeUserCollateral?.metadata);

    const durationIndex = getDurationIndex(form.strategyDuration);

    const collateralMint = getCollateralMint(activeUserCollateral);
    const collateralIdentifier = getCollateralIdentifier(activeUserCollateral);

    return async () => {
        if (
            !token ||
            !form.amount ||
            !form.collateralMint ||
            !form.collateralAmount ||
            !quote ||
            !activeUserCollateral ||
            !activeWallet ||
            !form.strategyDuration ||
            durationIndex === undefined ||
            !collateralMint ||
            !collateralIdentifier
        )
            return Result.errFromMessage(LOADING_ERROR);

        const clmmPositionType = getClmmPositionType(activeUserCollateral.clmmPosition ?? undefined);
        const isClmm = isClmmCollateral(activeUserCollateral);

        const amount = (() => {
            // wp positions always are balance of 1
            if (clmmPositionType) return 1;
            return form.collateralAmount;
        })();

        const metadata = activeUserCollateral.metadata;
        const assetData = getAssetData(metadata, isClmm ? getClmmPositionMint(form.clmmPosition) : metadata.mint);

        if (!assetData) return Result.errFromMessage("Invalid Collateral Asset");

        const params: CreateLoanTxnParams = {
            borrower: activeWallet,
            depositCollateral: [
                {
                    collateralAmount: uiAmountToLamports(amount, metadata.decimals),
                    collateralAssetData: assetData
                }
            ],
            principalRequested: [
                {
                    ledgerIndex: 0,
                    principalAmount: uiAmountToLamports(form.amount, token.decimals),
                    principalMint: token.mint,
                    strategy: quote.lendingStrategyAddress,
                    durationIndex,
                    expectedLoanValues: {
                        expectedApy: decimalsToBps(quote.apy),
                        expectedLqt: [decimalsToBps(quote.liquidationThreshold), 0, 0, 0, 0] //TODO: handle multiple ledgers
                    }
                }
            ],
            unstakeInstructions: getVaultLpStakeUnstakeIxParams({
                vault: activeUserCollateral?.oracle?.oracleAccount,
                amountLp: uiAmountToLamports(form.collateralAmount, activeUserCollateral?.metadata.decimals),
                skip: !isVaultLpCollateral(activeUserCollateral?.metadata)
            }),
            assetIndexGuidance: [],
            loanNonce
        };

        return await send(fillOrder, params, {
            alerter: { generationEstimateSeconds: 8, showProgress: true },
            messageOverrides: { errorMessageMap: BORROW_ERRORS },
            sendOptions: {
                onSuccess: () =>
                    setForm((prev) => ({
                        ...prev,
                        amount: undefined,
                        collateralAmount: undefined,
                        mode: BorrowMode.InputCollateral,
                        ltvMultiplier: 0
                    }))
            },
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitMarketBorrowOrder,
                params: trackSubmitMarketBorrowOrder({
                    params,
                    duration: quote,
                    source: isDialog ? "dialog" : "detail"
                })
            }
        });
    };
}

const MAX_TOTAL_TICKS = 50;
const MAX_FILL_TICKS = 20;
const MIN_TICK_DENSITY = 10;
export function useRatesTicks({
    orderbookQuotes,
    tickSizeBps = 1
}: {
    orderbookQuotes: OrderbookQuote[] | undefined;
    tickSizeBps?: number;
}) {
    if (!orderbookQuotes) return undefined;

    const allTicksMap = orderbookQuotes.reduce((map, curr) => {
        if (map.size > MAX_TOTAL_TICKS) return map;

        // use bps to prevent JS underflows
        const apyBps = decimalsToBps(curr.apy);
        const tickBps = Math.floor(apyBps / tickSizeBps) * tickSizeBps;

        const prev = map.get(tickBps);

        const totalPrincipal = (prev?.totalPrincipal ?? 0) + curr.sumPrincipalAvailable;
        const maxPrincipal = Math.max(prev?.maxPrincipal ?? 0, curr.maxPrincipalAvailable);

        map.set(tickBps, { totalPrincipal, maxPrincipal });
        return map;
    }, new Map<number, { totalPrincipal: number; maxPrincipal: number }>());

    const minTick = Math.min(...allTicksMap.keys());
    const maxTick = Math.max(...allTicksMap.keys());

    const estimatedAdditionalTicks = (maxTick - minTick) / tickSizeBps;

    if (orderbookQuotes.length > MIN_TICK_DENSITY && estimatedAdditionalTicks < MAX_FILL_TICKS) {
        // fill any 0 amount ticks
        for (let tick = minTick; tick < maxTick; tick += tickSizeBps) {
            if (allTicksMap.size > MAX_TOTAL_TICKS) break;
            const prev = allTicksMap.get(tick);
            if (prev) continue;
            allTicksMap.set(tick, { totalPrincipal: 0, maxPrincipal: 0 });
        }
    }

    let cumulativePrincipal = 0;
    return Array.from(allTicksMap.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([apy, { totalPrincipal, maxPrincipal }]): RateTick => {
            cumulativePrincipal += totalPrincipal;
            return {
                apy: bpsToUiDecimals(apy),
                totalPrincipal,
                maxPrincipal,
                cumulativePrincipal
            };
        })
        .filter((a) => !!a.maxPrincipal)
        .sort((a, b) => a.apy - b.apy);
}

export function getDefaultCollateral({
    principalMint,
    returnAll = false
}: {
    principalMint: string | undefined;
    returnAll?: boolean;
}) {
    if (returnAll) return null;
    return principalMint === USDC_MINT ? JLP_MINT : USDC_MINT;
}

export function useMarketCapDetails() {
    const { borrowCap, principalStats: data, token } = useMarketContext();

    return useMemo(() => {
        if (!borrowCap || !token || !data) return undefined;
        const { principalUtilized } = data.principalStats;

        return getBorrowCapDetails({ borrowCap, token, principalUtilized });
    }, [borrowCap, data, token]);
}

export function useCollateralTokensByTag(collateral: CollateralWithTerms[] | undefined) {
    const tokensByTags = useMemo(() => {
        if (!collateral) return undefined;
        const tagMap = new Map<TokenListTag, CollateralWithTerms[]>();
        const usedTokenMints = new Set();
        for (const c of collateral) {
            for (const tag of c.metadata.tags ?? []) {
                if (!getTokenTagMetadata(tag)) continue;
                const prev = tagMap.get(tag) ?? [];
                usedTokenMints.add(c.metadata.mint);
                tagMap.set(tag, [...prev, c]);
            }
        }

        if (usedTokenMints.size !== collateral.length) {
            tagMap.set(
                TokenListTag.Misc,
                collateral.filter((c) => !usedTokenMints.has(c.metadata.mint))
            );
        }

        return Array.from(tagMap.entries())
            .map(([tag, tokens]) => ({
                tokens: tokens.sort((a, b) =>
                    BsMetaUtil.getSymbol(a.metadata).localeCompare(BsMetaUtil.getSymbol(b.metadata))
                ),
                tag: getTokenTagMetadata(tag)
            }))
            .filter(filterNullableRecord)
            .filter(({ tag }) => !tag.hideInCategories)
            .sort((a, b) => a.tag.sortPosition - b.tag.sortPosition);
    }, [collateral]);

    return { tokensByTags };
}

export function useMarketCollateral(props?: Partial<MarketProps>) {
    const { principalMint } = useMarketContext();
    return useMarketCollateralFromProps({
        ...props,
        principalMint: props?.principalMint ?? principalMint
    } as MarketProps);
}

export function useMarketCollateralFromProps({ principalMint, requireMarketInformation }: MarketProps) {
    const collateral = useSupportedCollateral(principalMint, { requireMarketInformation: requireMarketInformation });
    return collateral;
}

const selectNapoleonApiQueries = (state: RootState) => state.marketsPublicApi.queries;

const allowedEndpoints = ["maxQuotes", "bestQuotes"];
const selectFetches = createSelector([selectNapoleonApiQueries], (queries) =>
    Object.values(queries).some(
        (e) => e?.status !== QueryStatus.fulfilled && e?.endpointName && allowedEndpoints.includes(e?.endpointName)
    )
);

const selectMaxQuotes = createSelector([selectNapoleonApiQueries], (queries) =>
    Object.values(queries).some((e) => e?.status !== QueryStatus.fulfilled && e?.endpointName === "maxQuotes")
);

export function useNapoleonFetching() {
    return useSelector(selectFetches);
}

export function useMaxQuotesFetching() {
    return useSelector(selectMaxQuotes);
}

const selectErrors = createSelector([selectNapoleonApiQueries], (queries) =>
    Object.values(queries).some((e) => e?.error && allowedEndpoints.includes(e.endpointName))
);
export function useNapoleonErrors() {
    return useSelector(selectErrors);
}

export function useOpenGuideOnLoad({ view }: { view: RoleView }) {
    const { open } = useMarketDialog();
    const { isDialog } = useMarketContext();
    const { steps, isLoading } = useUserOnboardingStep([OnboardingStep.Borrow, OnboardingStep.Lend]);

    useEffect(() => {
        // if the dialog is already open, don't open it again

        if (isLoading) return;

        if (view === RoleView.Borrow && !steps.includes(OnboardingStep.Borrow)) {
            return open(AppDialog.MarketGuide, { mode: MarketGuideMode.Borrow, isForced: true });
        }

        if (view === RoleView.Lend && !steps.includes(OnboardingStep.Lend)) {
            return open(AppDialog.MarketGuide, { mode: MarketGuideMode.Lend, isForced: true });
        }
    }, [isDialog, isLoading, open, steps, view]);
}

// Wrap app dialog to support seamless internal dialogs
export function useMarketDialog() {
    const marketContext = useMarketContextOptional();
    const appDialog = useAppDialog();
    const isDialog = !!marketContext?.isDialog;

    function open<T extends AppDialog>(type: T, data: AppDialogData<T>) {
        if (marketContext?.isDialog) {
            return marketContext.setInternalOpenDialog({ type, data });
        }
        return appDialog.open(type, data);
    }

    function close() {
        if (marketContext?.isDialog) {
            return marketContext.setInternalOpenDialog(null);
        }
        return appDialog.close();
    }

    function getData<T extends AppDialog>(identifier: T): AppDialogData<T> | undefined {
        if (marketContext?.isDialog) {
            if (!marketContext.internalOpenDialog) return undefined;
            return marketContext.internalOpenDialog.data as AppDialogData<T>;
        }
        return appDialog.getData(identifier);
    }

    return { open, close, getData, isDialog };
}

export function useSelectMarketPrincipal() {
    const { open } = useMarketDialog();
    const { selectToken, view } = useMarketContext();
    const { principalTokens } = useTokenListContext();
    const { trackWithProps } = useTrack();

    if (!selectToken || !principalTokens) return null;

    const selectTokenWithTracking = (token: TokenListMetadata): void => {
        trackWithProps(TrackEventWithProps.SelectBorrowPrincipal, {
            tokenSymbol: BsMetaUtil.getSymbol(token)
        });
        selectToken(token);
    };

    return () =>
        open(AppDialog.SelectToken, {
            header: `Select token to ${view.toLowerCase()}`,
            tokens: principalTokens,
            setToken: selectTokenWithTracking
        });
}

// function presetFormToMixpanelFormat(
//     collateralMints: string[],
//     serializedStrategyDurationToApy: Map<string, number | undefined>
// ): FormattedOfferTerms {
//     const terms: { [key: string]: number } = {};

//     for (const [serializedDuration, apy] of serializedStrategyDurationToApy) {
//         const duration = deserializeStrategyDurationOrUndefined(serializedDuration);
//         if (!duration || apy === undefined) continue;

//         const formattedDuration = formatDurationWithType(duration);
//         terms[formattedDuration] = percentDecimalsToUi(apy);
//     }

//     return {
//         collateral: collateralMints,
//         terms
//     };
// }

export function getCollateralTerms(
    form: MarketLendForm,
    getMarketInformationsByCollateralMint: (mint: string) => ParsedMarketInformationWithLtvInfo[] | undefined
): Map<number, [number, number][]> {
    const collateralTerms = new Map<number, [number, number][]>();
    for (const mint of form.collateral) {
        const marketInfos = getMarketInformationsByCollateralMint(mint);
        if (!marketInfos) continue;
        const targetMarketInfo = form.selectedMarketInfoAddress
            ? marketInfos.find((mi) => mi.marketInformation.address === form.selectedMarketInfoAddress)
            : getZcMarketInformation(marketInfos);
        if (!targetMarketInfo) continue;
        const ltvInfo = targetMarketInfo.marketInformationAssetInfo.find((asset) => asset.assetIdentifier === mint);
        if (!ltvInfo) continue;

        for (const [serializeDuration, apy] of form.strategyDurationToApy) {
            const deserializedDuration = deserializeStrategyDurationOrUndefined(serializeDuration);
            if (!deserializedDuration) continue;
            const durationIndex = getDurationIndex(deserializedDuration);
            if (durationIndex === undefined) continue;
            const formApy = apy ? uiPercentToBps(apy) : undefined;
            if (!formApy) continue;
            const existing = collateralTerms.get(formApy);
            if (existing) {
                existing.push([ltvInfo.index, durationIndex]);
            } else {
                collateralTerms.set(formApy, [[ltvInfo.index, durationIndex]]);
            }
        }
    }
    return collateralTerms;
}
